import { userRoles } from '@shared/config/user-roles.config';
import { AuthService } from '@core/auth.service';
import { Component, OnInit, Inject } from '@angular/core';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { ToasterService } from '@services/toaster.service';
import { Roles } from '@constants/global.enum';

@Component({
  selector: 'app-login-callback',
  standalone: true,
  imports: [],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginCallbackComponent implements OnInit {
  constructor(
    @Inject(OKTA_AUTH) public oktaAuth: OktaAuth,
    private toasterService: ToasterService,
    private authService: AuthService,
  ) {}

  async handleLoginRedirect() {
    // Authorization Code + PKCE returns `?code=...&state=...` (query), while the
    // legacy implicit flow returned `#id_token=...` (fragment). isLoginRedirect()
    // detects both; parseFromUrl() below performs the PKCE code->token exchange.
    if (this.oktaAuth.isLoginRedirect()) {
      const originalUriRaw = this.oktaAuth.getOriginalUri();
      let originalUri: string | null = null;
      if (originalUriRaw) {
        try {
          originalUri = new URL(originalUriRaw, globalThis.location.origin).pathname;
        } catch {
          originalUri = originalUriRaw;
        }
      }
      const savedRoute = sessionStorage.getItem('preAuthRoute');
      try {
        const tokens = await this.oktaAuth.token.parseFromUrl();
        this.oktaAuth.tokenManager.setTokens(tokens.tokens);
      } catch (parseError) {
        // The authorization code was already exchanged by a parallel/duplicate
        // pass (okta throws "Could not load PKCE codeVerifier from storage").
        // If we ended up authenticated anyway, that's a benign double-flow —
        // continue and route the user in. Otherwise rethrow to the retry logic.
        if (!(await this.oktaAuth.isAuthenticated())) {
          throw parseError;
        }
      }
      const isAuth = await this.oktaAuth.isAuthenticated();
      if (isAuth) {
        this.authService.oktaUserProfile = await this.oktaAuth.getUser();
        if (!this.authService.oktaUserProfile.email)
          throw new Error('User profile email not found.');
        const employee = await this.authService.getEmployeeDetails();
        if (!employee) throw new Error('Employee details not found.');
        const poiType =
          this.authService.getEmployeeType() as keyof typeof userRoles;

        const routes =
          poiType === Roles.IOP ? userRoles['EMP'] : userRoles[poiType]; // IOP hardcoded email id's have access to all the routes
        if (!routes)
          throw new Error('ACCESS_DENIED: You do not have access to this application.');
        const token = this.oktaAuth.getAccessToken();
        if (token) sessionStorage.setItem('token', token);

        // Set default navigation based only on isReporties and isOpsTeam
        let defaultRoute = '';

        if (employee?.isReporties && employee?.isOpsTeam) {
          // Both permissions: Team Management (highest priority)
          defaultRoute = '/team-management';
        } else if (employee?.isReporties) {
          // Only reporties: Team Management
          defaultRoute = '/team-management';
        } else if (employee?.isOpsTeam) {
          // Only ops team: OPS Dashboard
          defaultRoute = '/ops-vendor-management';
        } else {
          // No reportees, no ops responsibilities. Check role and redirect
          // accordingly. CBRE employees (EMPLOYEE or EMP without reportees)
          // land on iSkill — Hiring Manager / vendor-management shows empty
          // tables + zero counters for them and reads as broken.
          const userRole = this.authService.getEmployeeType();
          if (userRole === Roles.vendor) {
            defaultRoute = '/vendorflow-dashboard';
          } else if (userRole === Roles.CWR) {
            defaultRoute = '/vendor-employee';
          } else if (
            (userRole === Roles.EMPLOYEE || userRole === Roles.EMP) &&
            employee?.isScrumMaster
          ) {
            // Scrum masters without reportees would otherwise land on /iskill,
            // where the main sidebar (and its Productivity item) never renders —
            // they reported being unable to find Productivity at all. Land them
            // on the tracker itself; the left nav there shows iSkill too.
            defaultRoute = '/productivity';
          } else if (userRole === Roles.EMPLOYEE || userRole === Roles.EMP) {
            defaultRoute = '/iskill';
          } else {
            // Any other role (IOP, unknowns): fall back to their configured
            // root — keeps existing behavior for admins / edge cases.
            defaultRoute = `/${routes.root}`;
          }
        }

        // Get route from multiple sources for maximum reliability
        const lastVisitedRoute = sessionStorage.getItem('lastVisitedRoute');
        const currentRoute = sessionStorage.getItem('currentRoute');

        // Check if we have a preserved route to avoid auto-redirect
        const willUsePreservedRoute =
          savedRoute ||
          currentRoute ||
          lastVisitedRoute ||
          (originalUri &&
            originalUri !== '/' &&
            originalUri !== '/login' &&
            originalUri !== '/login/callback');

        // Only remove sideNavHasNavigated if we're going to default route
        // If we have a preserved route, keep the navigation flag to prevent auto-redirect
        if (!willUsePreservedRoute) {
          sessionStorage.removeItem('sideNavHasNavigated');
        }

        // Priority: savedRoute (auth guard) > currentRoute (auth guard backup) > lastVisitedRoute (app component) > originalUri (Okta) > defaultRoute
        let targetRoute = defaultRoute;

        if (
          originalUri &&
          originalUri !== '/' &&
          originalUri !== '/login' &&
          originalUri !== '/login/callback'
        ) {
          targetRoute = originalUri;
        }

        if (
          lastVisitedRoute &&
          lastVisitedRoute !== '/' &&
          lastVisitedRoute !== '/login' &&
          lastVisitedRoute !== '/login/callback'
        ) {
          targetRoute = lastVisitedRoute;
        }

        if (
          currentRoute &&
          currentRoute !== '/' &&
          currentRoute !== '/login' &&
          currentRoute !== '/login/callback'
        ) {
          targetRoute = currentRoute;
        }

        if (
          savedRoute &&
          savedRoute !== '/' &&
          savedRoute !== '/login' &&
          savedRoute !== '/login/callback'
        ) {
          targetRoute = savedRoute;
        }

        // Validate that user has access to the target route
        if (
          targetRoute !== defaultRoute &&
          !this.authService.hasAccessToRoute(targetRoute)
        ) {
          console.warn(
            'User does not have access to saved route:',
            targetRoute,
            '. Using default route:',
            defaultRoute,
          );
          targetRoute = defaultRoute;
        }

        // Clean up stored routes
        sessionStorage.removeItem('preAuthRoute');
        sessionStorage.removeItem('currentRoute');

        globalThis.location.replace(targetRoute);
      } else {
        throw new Error('User is not authenticated.');
      }
    } else {
      // Not a login redirect — the user navigated to /login/callback directly
      // (bookmark, back button) or the state was lost. If they already hold a
      // valid session, send them into the app; otherwise back to /login.
      if (await this.oktaAuth.isAuthenticated()) {
        globalThis.location.replace('/');
        return;
      }
      this.authService.reAttemptOktaToken();
      globalThis.location.href = '/login';
    }
  }

  async ngOnInit() {
    const RETRY_KEY = 'auth-retry-count';
    try {
      // Watchdog: no await in the login flow is allowed to hang the page
      // forever (the "stuck on the loading spinner" reports). If the whole
      // flow hasn't finished in 30s, treat it as a failure and recover.
      await Promise.race([
        this.handleLoginRedirect(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('LOGIN_TIMEOUT: login flow exceeded 30s')), 30000)),
      ]);
      sessionStorage.removeItem(RETRY_KEY);
    } catch (error) {
      // Surface the cause — this catch used to swallow errors silently,
      // which made "stuck on the loading page" reports undiagnosable.
      console.error('[login-callback] login flow error:', error);
      const msg = error instanceof Error ? error.message : String(error);

      // Access denied is terminal — retrying won't grant access.
      if (msg.startsWith('ACCESS_DENIED')) {
        sessionStorage.removeItem(RETRY_KEY);
        this.toasterService.showError(
          'You do not have access to this application.', 'Access Denied');
        setTimeout(() => globalThis.location.replace('/login'), 2500);
        return;
      }

      // A parallel flow may have completed login even though this pass threw.
      try {
        if (await this.oktaAuth.isAuthenticated()) {
          sessionStorage.removeItem(RETRY_KEY);
          // The user IS authenticated — never strand them on the callback
          // spinner; hand off to the app root and let the guards route them.
          globalThis.location.replace('/');
          return;
        }
      } catch (checkError) {
        // isAuthenticated itself failed — fall through to the retry logic.
        console.error('[login-callback] isAuthenticated check failed:', checkError);
      }

      const retries = Number.parseInt(sessionStorage.getItem(RETRY_KEY) ?? '0', 10);
      if (retries >= 1) {
        sessionStorage.removeItem(RETRY_KEY);
        this.toasterService.showError('Login failed, please try again.', 'Login Error');
        // Give the toast a beat, then land on /login so the user can retry
        // instead of sitting on the spinner.
        setTimeout(() => globalThis.location.replace('/login'), 2500);
        return;
      }
      sessionStorage.setItem(RETRY_KEY, String(retries + 1));

      // Purge any half-written Okta/PKCE transaction state so the retry
      // starts clean — this is what users were achieving manually by
      // closing the window (sessionStorage dies with it).
      [localStorage, sessionStorage].forEach(store => {
        Object.keys(store)
          .filter(k => k.startsWith('okta-') || k.startsWith('pkce-'))
          .forEach(k => store.removeItem(k));
      });
      globalThis.location.replace('/login');
    }
  }
}
